// Simple toast utility functions
let toastContainer: HTMLDivElement | null = null;

interface ToastOptions {
  duration?: number;
  position?: 'top' | 'bottom' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}

const createToastContainer = (position: string = 'top-right'): HTMLDivElement => {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'fixed z-50';
    
    // Position classes
    const positionClasses: Record<string, string> = {
      'top': 'top-4 left-1/2 transform -translate-x-1/2',
      'bottom': 'bottom-4 left-1/2 transform -translate-x-1/2',
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4'
    };
    
    toastContainer.className += ' ' + (positionClasses[position] || positionClasses['top-right']);
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
};

const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning', options: ToastOptions = {}) => {
  const container = createToastContainer(options.position);
  const toast = document.createElement('div');
  
  // Base classes
  toast.className = 'mb-2 p-4 rounded-lg shadow-lg flex items-center space-x-3 min-w-[300px] max-w-md animate-slide-in';
  
  // Type-specific classes
  const typeClasses: Record<string, string> = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    info: 'bg-blue-500 text-white',
    warning: 'bg-yellow-500 text-white'
  };
  
  toast.className += ' ' + typeClasses[type];
  
  // Icons
  const icons: Record<string, string> = {
    success: '✓',
    error: '✕',
    info: 'i',
    warning: '!'
  };
  
  toast.innerHTML = `
    <div class="flex-shrink-0 w-6 h-6 rounded-full bg-white bg-opacity-30 flex items-center justify-center">
      <span class="text-sm font-bold">${icons[type]}</span>
    </div>
    <div class="flex-1">
      <p class="text-sm font-medium">${message}</p>
    </div>
  `;
  
  container.appendChild(toast);
  
  // Add slide-in animation
  setTimeout(() => {
    toast.classList.add('opacity-100', 'translate-y-0');
  }, 10);
  
  // Remove after duration
  const duration = options.duration || 3000;
  setTimeout(() => {
    toast.classList.add('opacity-0', 'translate-y-2');
    setTimeout(() => {
      toast.remove();
      // Remove container if empty
      if (container.children.length === 0) {
        container.remove();
        toastContainer = null;
      }
    }, 300);
  }, duration);
};

// Add CSS for animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slide-in {
    from {
      opacity: 0;
      transform: translateY(-1rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`;
document.head.appendChild(style);

// Export convenience functions
export const showSuccess = (message: string, options?: ToastOptions) => {
  showToast(message, 'success', options);
};

export const showError = (message: string, options?: ToastOptions) => {
  showToast(message, 'error', options);
};

export const showInfo = (message: string, options?: ToastOptions) => {
  showToast(message, 'info', options);
};

export const showWarning = (message: string, options?: ToastOptions) => {
  showToast(message, 'warning', options);
};

export default {
  showSuccess,
  showError,
  showInfo,
  showWarning
};